import { useState, useMemo, useCallback, useRef, useEffect } from 'react';

/**
 * GroupPicker — searchable checkbox table for assigning groups to a resident.
 *
 * Used inside the ApproveAddDialog during move-in approval.
 *
 * @param {{
 *   groups: Array<{ resident_group_id: number, resident_group_name: string, resident_description: string }>,
 *   selectedIds: string[],
 *   onSelectionChange: (ids: string[]) => void,
 * }} props
 */
const GroupPicker = ({ groups, selectedIds, onSelectionChange }) => {
  const [search, setSearch] = useState('');
  const [showChecked, setShowChecked] = useState(false);
  const [sortOrder, setSortOrder] = useState('asc');

  // Reset search/filter when groups change (dialog reopened)
  const prevGroupsRef = useRef(groups);
  useEffect(() => {
    if (prevGroupsRef.current !== groups) {
      setSearch('');
      setShowChecked(false);
      prevGroupsRef.current = groups;
    }
  }, [groups]);

  const filtered = useMemo(() => {
    let result = [...(groups || [])];

    // Text search
    if (search.trim()) {
      const s = search.toLowerCase();
      result = result.filter(
        (g) =>
          g.resident_group_name?.toLowerCase().includes(s) ||
          g.resident_description?.toLowerCase().includes(s)
      );
    }

    // Show checked only
    if (showChecked) {
      result = result.filter((g) =>
        selectedIds.includes(g.resident_group_id.toString())
      );
    }

    // Sort by group name
    result.sort((a, b) => {
      const cmp = (a.resident_group_name || '').localeCompare(
        b.resident_group_name || '',
        undefined,
        { sensitivity: 'base' }
      );
      return sortOrder === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [groups, search, showChecked, selectedIds, sortOrder]);

  const allSelected =
    filtered.length > 0 &&
    filtered.every((g) => selectedIds.includes(g.resident_group_id.toString()));

  const handleToggle = useCallback(
    (groupId) => {
      const id = groupId.toString();
      const next = selectedIds.includes(id)
        ? selectedIds.filter((x) => x !== id)
        : [...selectedIds, id];
      onSelectionChange(next);
    },
    [selectedIds, onSelectionChange]
  );

  const handleSelectAll = useCallback(() => {
    const filteredIds = filtered.map((g) => g.resident_group_id.toString());
    if (allSelected) {
      // Deselect all filtered
      onSelectionChange(selectedIds.filter((id) => !filteredIds.includes(id)));
    } else {
      // Select all filtered
      onSelectionChange([...new Set([...selectedIds, ...filteredIds])]);
    }
  }, [filtered, allSelected, selectedIds, onSelectionChange]);

  const toggleSort = useCallback(() => {
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  }, []);

  return (
    <div className="ehr-group-picker">
      <div className="ehr-group-picker__header">
        <h4 className="ehr-group-picker__title">Groups</h4>
        <div className="ehr-group-picker__controls">
          <div className="dp-search" style={{ flex: '1' }}>
            <svg
              className="dp-icon-md dp-search__icon"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
              />
            </svg>
            <input
              type="search"
              placeholder="Search groups..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="dp-search__input"
              aria-label="Search groups"
            />
          </div>
          <label className="ehr-group-picker__toggle">
            <input
              type="checkbox"
              checked={showChecked}
              onChange={(e) => setShowChecked(e.target.checked)}
              className="dp-checkbox"
            />
            <span className="ehr-group-picker__toggle-label">Show Checked</span>
          </label>
        </div>
      </div>

      <div className="ehr-group-picker__scroll">
        <table className="dp-table">
          <thead className="dp-thead">
            <tr>
              <th scope="col" className="dp-th dp-th--checkbox">
                <input
                  type="checkbox"
                  checked={allSelected && filtered.length > 0}
                  onChange={handleSelectAll}
                  className="dp-checkbox"
                  aria-label="Select all groups"
                />
              </th>
              <th scope="col" className="dp-th">
                <button
                  type="button"
                  onClick={toggleSort}
                  className="dp-th__sort-btn"
                  aria-label="Sort by group name"
                >
                  Group
                  <svg
                    className="dp-icon-sm dp-sort-icon dp-sort-icon--active"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d={
                        sortOrder === 'asc'
                          ? 'M4.5 15.75l7.5-7.5 7.5 7.5'
                          : 'M19.5 8.25l-7.5 7.5-7.5-7.5'
                      }
                    />
                  </svg>
                </button>
              </th>
              <th scope="col" className="dp-th">Description</th>
            </tr>
          </thead>
          <tbody className="dp-tbody">
            {filtered.map((g) => {
              const id = g.resident_group_id.toString();
              const checked = selectedIds.includes(id);
              return (
                <tr
                  key={g.resident_group_id}
                  className={`dp-tr ${checked ? 'is-selected' : ''}`}
                  onClick={() => handleToggle(g.resident_group_id)}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="dp-td dp-td--checkbox">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleToggle(g.resident_group_id)}
                      className="dp-checkbox"
                      aria-label={`Select ${g.resident_group_name}`}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td className="dp-td">
                    <span className="dp-cell-text">{g.resident_group_name}</span>
                  </td>
                  <td className="dp-td">
                    <span className="dp-cell-text">
                      {g.resident_description || '\u2014'}
                    </span>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 ? (
              <tr className="dp-tr">
                <td className="dp-td" colSpan={3} style={{ textAlign: 'center', padding: '1rem' }}>
                  <span className="dp-cell-text--muted">No groups found</span>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GroupPicker;
